/**
 * [Gurumi 리사이즈 이미지]
 * 역할: yesmoney ResizableImage 포팅
 * 작성/수정일: 2026-07-29
 */
import Image from '@tiptap/extension-image';
import { mergeAttributes, ResizableNodeView } from '@tiptap/core';
import { resolveEditorImageUrl } from '../image-upload.js';
import { clampEditorMediaSize, syncResizableNodeMaxSize } from '../utils.js';

function resolveAlignment(value) {
  return value === 'left' || value === 'right' || value === 'center' ? value : 'center';
}

function isUploadingAttr(value) {
  return value === true || value === 'true' || value === 1 || value === '1';
}

function applyAlignmentToContainer(container, alignment) {
  container.style.display = 'flex';
  container.style.width = '100%';
  container.style.maxWidth = '100%';
  container.style.boxSizing = 'border-box';
  container.style.justifyContent =
    alignment === 'left' ? 'flex-start' : alignment === 'right' ? 'flex-end' : 'center';
  container.dataset.align = alignment;
}

function syncImageElement(el, node) {
  const alignment = resolveAlignment(node.attrs.alignment);
  el.setAttribute('data-align', alignment);
  el.classList.add('editor-image');
  el.setAttribute('loading', 'lazy');
  const src = resolveEditorImageUrl(String(node.attrs.src ?? ''));
  if (src && el.getAttribute('src') !== src) el.src = src;
  el.alt = String(node.attrs.alt ?? '');
  const width = Number(node.attrs.width ?? 0);
  if (width > 0) {
    el.style.width = `${width}px`;
    el.style.height = 'auto';
  }
  if (isUploadingAttr(node.attrs.uploading)) el.setAttribute('data-uploading', '1');
  else el.removeAttribute('data-uploading');
}

function syncUploadingState(container, image, overlay, node) {
  const uploading = isUploadingAttr(node.attrs.uploading);
  container.classList.toggle('is-uploading', uploading);
  if (uploading) {
    container.style.position = 'relative';
    container.style.pointerEvents = 'none';
    overlay.hidden = false;
  } else {
    container.style.pointerEvents = '';
    overlay.hidden = true;
  }
}

export const ResizableImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      alignment: {
        default: 'center',
        parseHTML: (el) => el.getAttribute('data-align') || 'center',
        renderHTML: (attrs) => ({ 'data-align': resolveAlignment(attrs.alignment) }),
      },
      uploading: {
        default: false,
        parseHTML: () => false,
        renderHTML: (attrs) => (isUploadingAttr(attrs.uploading) ? { 'data-uploading': '1' } : {}),
      },
    };
  },

  addNodeView() {
    const resize = this.options.resize;
    if (typeof document === 'undefined' || !resize?.enabled) return null;

    const { directions, minWidth, minHeight, alwaysPreserveAspectRatio } = resize;

    return ({ node, getPos, HTMLAttributes, editor }) => {
      const el = document.createElement('img');
      el.draggable = false;
      const merged = mergeAttributes(this.options.HTMLAttributes ?? {}, HTMLAttributes);
      Object.entries(merged).forEach(([key, value]) => {
        if (value == null || key === 'width' || key === 'height') return;
        el.setAttribute(key, String(value));
      });
      if (merged.src != null) el.src = resolveEditorImageUrl(String(merged.src));
      syncImageElement(el, node);

      const overlay = document.createElement('div');
      overlay.className = 'editor-image-upload-overlay';
      overlay.hidden = true;
      const overlayLabel = document.createElement('span');
      overlayLabel.className = 'editor-image-upload-label';
      overlayLabel.textContent = '등록 중…';
      overlay.appendChild(overlayLabel);

      const nodeView = new ResizableNodeView({
        element: el,
        editor,
        node,
        getPos,
        onResize: (width, height) => {
          // [미디어 리사이즈] 드래그 중 에디터 폭 초과 방지 — 작성/수정일: 2026-07-29
          syncResizableNodeMaxSize(nodeView, editor, minWidth);
          const clamped = clampEditorMediaSize(editor, width, height, minWidth);
          el.style.width = `${clamped.width}px`;
          el.style.height = `${clamped.height}px`;
          el.style.maxWidth = '100%';
        },
        onCommit: (width, height) => {
          const pos = getPos();
          if (pos === undefined) return;
          const clamped = clampEditorMediaSize(editor, width, height, minWidth);
          el.style.width = `${clamped.width}px`;
          el.style.maxWidth = '100%';
          editor
            .chain()
            .setNodeSelection(pos)
            .updateAttributes(this.name, { width: clamped.width, height: clamped.height })
            .run();
        },
        onUpdate: (updatedNode) => {
          if (updatedNode.type !== this.type) return false;
          syncImageElement(el, updatedNode);
          syncUploadingState(nodeView.dom, el, overlay, updatedNode);
          applyAlignmentToContainer(nodeView.dom, resolveAlignment(updatedNode.attrs.alignment));
          return true;
        },
        options: {
          directions,
          min: { width: minWidth, height: minHeight },
          max: {
            width: syncResizableNodeMaxSize({ maxSize: {} }, editor, minWidth),
            height: 4000,
          },
          preserveAspectRatio: alwaysPreserveAspectRatio === true,
        },
      });

      const dom = nodeView.dom;
      applyAlignmentToContainer(dom, resolveAlignment(node.attrs.alignment));
      dom.appendChild(overlay);
      syncUploadingState(dom, el, overlay, node);
      dom.style.visibility = 'hidden';
      dom.style.pointerEvents = 'none';
      // [미디어 리사이즈] 핸들 드래그 시작 시 현재 에디터 폭으로 max 갱신 — 작성/수정일: 2026-07-29
      dom.addEventListener(
        'pointerdown',
        () => {
          syncResizableNodeMaxSize(nodeView, editor, minWidth);
        },
        true,
      );
      syncResizableNodeMaxSize(nodeView, editor, minWidth);
      el.onload = () => {
        dom.style.visibility = '';
        if (!isUploadingAttr(node.attrs.uploading)) dom.style.pointerEvents = '';
      };

      return nodeView;
    };
  },
}).configure({
  allowBase64: false,
  resize: {
    enabled: true,
    directions: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
    minWidth: 80,
    minHeight: 45,
    alwaysPreserveAspectRatio: true,
  },
  HTMLAttributes: { loading: 'lazy', class: 'editor-image' },
});
